import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import '../styles/SearchPO.css';

// Quantities can only be corrected until the order is checked — after that it
// has already been sent to Loken. Mirrors ADJUSTABLE_PO_STATUSES in the backend.
const ADJUSTABLE_STATUSES = ['pending', 'in_process', 'picked'];

const ADJUSTMENT_REASONS = [
  { value: 'falta_stock', label: 'Falta de stock' },
  { value: 'recuento', label: 'Recuento' },
  { value: 'error_pedido', label: 'Error en el pedido' },
  { value: 'otro', label: 'Otro' },
];

const SearchPO = () => {
  const [poNumber, setPoNumber] = useState('');
  const [poDetails, setPoDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [editReason, setEditReason] = useState('falta_stock');
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [adjustError, setAdjustError] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  const fetchPoDetails = async (number) => {
    const response = await fetch(`${API_BASE}/get-po-details/?po_number=${number}`, {
      headers: {
        'Authorization': `Token ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Error al buscar la orden');
    }

    return response.json();
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setPoDetails(null);
    setAdjustError(null);
    setEditingItemId(null);

    try {
      setPoDetails(await fetchPoDetails(poNumber));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (item) => {
    setEditingItemId(item.id);
    setEditQuantity(String(item.ordered_quantity));
    setEditReason('falta_stock');
    setEditNote('');
    setAdjustError(null);
  };

  const cancelEditing = () => {
    setEditingItemId(null);
    setAdjustError(null);
  };

  const handleAdjust = async (item) => {
    setSaving(true);
    setAdjustError(null);

    try {
      const response = await fetch(`${API_BASE}/adjust-item-quantity/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        },
        body: JSON.stringify({
          po_number: poDetails.po_number,
          item_id: item.id,
          new_quantity: editQuantity,
          reason: editReason,
          note: editNote,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setAdjustError(data.error || 'No se pudo actualizar la cantidad.');
        return;
      }

      // Re-read the order so quantities and the audit history stay in sync.
      setPoDetails(await fetchPoDetails(poDetails.po_number));
      setEditingItemId(null);
    } catch (err) {
      setAdjustError('No se pudo actualizar la cantidad. Revisa tu conexión.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return 'No disponible';
    // Split by decimal point and take only the first part
    const [time] = duration.split('.');
    // If the time has milliseconds, round up the seconds
    const [hours, minutes, seconds] = time.split(':');
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatCartCode = (code) => {
    if (!code) return '';
    if (code.startsWith('BASKET-')) {
      return `FRIO ${code.replace('BASKET-', '')}`;
    } else if (code.startsWith('CART-')) {
      return `ABARROTE ${code.replace('CART-', '')}`;
    }
    return code;
  };

  const formatCartStatus = (status) => {
    return status === 'in_use' ? 'En uso' : 'Libre';
  };

  return (
    <div className="search-po-container">
      <button className="search-po-back-button" onClick={() => navigate('/init')}>
        &#8592;
      </button>

      <h1 className="search-po-title">Buscar Orden de Compra</h1>

      <form onSubmit={handleSearch} className="search-po-form">
        <div className="form-group">
          <label htmlFor="poNumber">Número de Orden:</label>
          <input
            type="text"
            id="poNumber"
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            placeholder="Ingrese el número de orden"
            required
          />
        </div>
        <button type="submit" disabled={loading}>
          {loading ? 'Buscando...' : 'Buscar'}
        </button>
      </form>

      {error && <div className="search-po-error">{error}</div>}

      {poDetails && (
        <div className="po-details-container">
          <div className="po-header">
            <h2>Detalles de la Orden</h2>
            <div className="po-status">
              <span className={`status-badge ${poDetails.status}`}>
                {poDetails.status}
              </span>
            </div>
          </div>

          <div className="po-info-grid">
            <div className="info-section">
              <h3>Información General</h3>
              <p><strong>Número de Orden:</strong> {poDetails.po_number}</p>
              <p><strong>Fecha de Orden:</strong> {formatDate(poDetails.po_date)}</p>
              <p><strong>Estado:</strong> {poDetails.status}</p>
              <p><strong>Cliente:</strong> {poDetails.client.client_name}</p>
              <p><strong>Dirección:</strong> {poDetails.client.client_address}</p>
              <p><strong>Embalar:</strong> {poDetails.packing ? 'Sí' : 'No'}</p>
              {poDetails.cancel_reason && (
                <p><strong>Motivo de Cancelación:</strong> {poDetails.cancel_reason}</p>
              )}
            </div>

            <div className="info-section">
              <h3>Timeline</h3>
              <p><strong>Picking:</strong> {formatDate(poDetails.picked_at)}</p>
              <p><strong>Checking:</strong> {formatDate(poDetails.checked_at)}</p>
              <p><strong>Inicio Carga:</strong> {formatDate(poDetails.time_tracking?.loading_started_at)}</p>
              <p><strong>Fin Carga:</strong> {formatDate(poDetails.time_tracking?.loading_completed_at)}</p>
              <p><strong>Pago:</strong> {formatDate(poDetails.paid_at)}</p>
              <p><strong>Completado:</strong> {formatDate(poDetails.completed_at)}</p>
            </div>

            <div className="info-section">
              <h3>Tiempos de Proceso</h3>
              <p><strong>Creación a Picking:</strong> {formatDuration(poDetails.time_tracking?.creation_to_picking)}</p>
              <p><strong>Picking a Checking:</strong> {formatDuration(poDetails.time_tracking?.picking_to_checking)}</p>
              <p><strong>Checking a Pago:</strong> {formatDuration(poDetails.time_tracking?.checking_to_payment)}</p>
              <p><strong>Tiempo de Carga:</strong> {formatDuration(poDetails.time_tracking?.loading_time)}</p>
              <p><strong>Pago a Completado:</strong> {formatDuration(poDetails.time_tracking?.payment_to_completion)}</p>
            </div>
          </div>

          <div className="responsibilities-section">
            <h3>Responsables</h3>
            <div className="responsibilities-grid">
              {poDetails.responsibilities && poDetails.responsibilities.map((resp, index) => {
                // Use time_tracking data for loading/carga role
                const isLoadingRole = resp.role === 'carga';
                const startTime = isLoadingRole ? poDetails.time_tracking?.loading_started_at : resp.start_time;
                const completionTime = isLoadingRole ? poDetails.time_tracking?.loading_completed_at : resp.completion_time;
                const duration = isLoadingRole ? poDetails.time_tracking?.loading_time : resp.duration;

                return (
                  <div key={index} className="responsibility-card">
                    <h4>{resp.user}</h4>
                    <p><strong>Rol:</strong> {resp.role}</p>
                    <p><strong>Inicio:</strong> {formatDate(startTime)}</p>
                    <p><strong>Fin:</strong> {formatDate(completionTime)}</p>
                    <p><strong>Duración:</strong> {formatDuration(duration)}</p>
                    <p><strong>Errores:</strong> {resp.error_count}</p>
                    {resp.errors && resp.errors.length > 0 && (
                      <div className="errors-list">
                        {resp.errors.map((error, errIndex) => (
                          <div key={errIndex} className="error-item">
                            <p><strong>Tipo:</strong> {error.error_type}</p>
                            <p><strong>Descripción:</strong> {error.error_description}</p>
                            <p><strong>Hora:</strong> {formatDate(error.error_timestamp)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="items-section">
            <h3>Items</h3>
            {adjustError && <div className="search-po-error">{adjustError}</div>}
            <div className="items-grid">
              {poDetails.items && poDetails.items.map((item, index) => (
                <div key={index} className="item-card">
                  <h4>{item.sku_name}</h4>
                  <p><strong>SKU:</strong> {item.sku_code}</p>
                  <p><strong>Pedido:</strong> {item.ordered_quantity} - {item.format_name}</p>
                  <p><strong>Cantidad:</strong> {item.actual_quantity} - {item.format_name}</p>

                  {ADJUSTABLE_STATUSES.includes(poDetails.status) && (
                    editingItemId === item.id ? (
                      <div className="item-edit-form">
                        <label>
                          Nueva cantidad:
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            value={editQuantity}
                            onChange={(e) => setEditQuantity(e.target.value)}
                          />
                        </label>
                        <label>
                          Motivo:
                          <select value={editReason} onChange={(e) => setEditReason(e.target.value)}>
                            {ADJUSTMENT_REASONS.map((reason) => (
                              <option key={reason.value} value={reason.value}>{reason.label}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Nota (opcional):
                          <input
                            type="text"
                            value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                            placeholder="Detalle del ajuste"
                          />
                        </label>
                        <div className="item-edit-actions">
                          <button type="button" onClick={() => handleAdjust(item)} disabled={saving}>
                            {saving ? 'Guardando...' : 'Guardar'}
                          </button>
                          <button type="button" onClick={cancelEditing} disabled={saving}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="item-edit-button" onClick={() => startEditing(item)}>
                        Editar cantidad
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>

          {poDetails.adjustments && poDetails.adjustments.length > 0 && (
            <div className="adjustments-section">
              <h3>Ajustes de Cantidad</h3>
              <div className="adjustments-grid">
                {poDetails.adjustments.map((adjustment, index) => (
                  <div key={index} className="adjustment-card">
                    <h4>{adjustment.sku_code}</h4>
                    <p>
                      <strong>Pedido:</strong> {adjustment.old_ordered_quantity} → {adjustment.new_ordered_quantity}
                    </p>
                    {adjustment.new_actual_quantity !== null && (
                      <p>
                        <strong>Pickeado:</strong> {adjustment.old_actual_quantity} → {adjustment.new_actual_quantity}
                      </p>
                    )}
                    <p><strong>Motivo:</strong> {adjustment.reason}</p>
                    {adjustment.note && <p><strong>Nota:</strong> {adjustment.note}</p>}
                    <p><strong>Responsable:</strong> {adjustment.user}</p>
                    <p><strong>Fecha:</strong> {formatDate(adjustment.timestamp)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {poDetails.requests && poDetails.requests.length > 0 && (
            <div className="adjustments-section">
              <h3>Solicitudes de Modificación</h3>
              <div className="adjustments-grid">
                {poDetails.requests.map((req, index) => (
                  <div key={index} className="adjustment-card">
                    <h4>{req.sku_code}</h4>
                    <p><strong>Estado:</strong> {req.status}</p>
                    <p><strong>Solicitado:</strong> {req.requested_quantity}</p>
                    {req.approved_quantity !== null && (
                      <p><strong>Aprobado:</strong> {req.approved_quantity}</p>
                    )}
                    <p><strong>Picker:</strong> {req.requested_by}</p>
                    {req.resolved_by && <p><strong>Resuelto por:</strong> {req.resolved_by}</p>}
                    {req.note && <p><strong>Nota:</strong> {req.note}</p>}
                    {req.resolution_note && (
                      <p><strong>Respuesta:</strong> {req.resolution_note}</p>
                    )}
                    <p><strong>Fecha:</strong> {formatDate(req.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="carts-section">
            <h3>Carritos</h3>
            <div className="carts-grid">
              {poDetails.carts && poDetails.carts.map((cart, index) => (
                <div key={index} className="cart-card">
                  <h4>{formatCartCode(cart.cart_code)}</h4>
                  <p><strong>Estado:</strong> {formatCartStatus(cart.status)}</p>
                  <p><strong>Verificado:</strong> {cart.checked ? 'Sí' : 'No'}</p>
                  <p><strong>Cantidad de Skus:</strong> {cart.items_count}</p>
                </div>
              ))}
              {(!poDetails.carts || poDetails.carts.length === 0) && (
                <p>No hay carritos disponibles para esta orden.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPO;
